/**
 * `/credit-notes` — the list.
 *
 * Modelled on `InvoicesPage` line for line where the two ask the same question:
 * URL-backed id filters, column funnels through `useTableFilters`, the
 * loading / empty / error triad, and one table above `md` with a stacked card
 * list below it.
 *
 * ── WHAT IS DIFFERENT ABOUT A CREDIT NOTE ────────────────────────────────────
 *
 * 🔴 **THREE MONEY COLUMNS, NOT ONE, AND TWO OF THEM ARE DERIVED.** `amount` is
 * the note's `grandTotal`; `allocatedTotal` is the sum of its allocations;
 * `remainingAmount` is the first minus the second, computed on read because
 * **there is no `remaining_amount` column and there never will be** (backend
 * migration 199). Every one of them is an exact DECIMAL STRING and goes through
 * `formatDecimalString`. ⛔ **Never `Number()`**, never a total across rows.
 *
 * 🔴 **`remainingAmount` IS REPORTED ON A DRAFT, AND IT IS NOT "CREDIT
 * AVAILABLE".** A draft carries its whole value there and can be allocated
 * against nothing; what decides is `spendable`, a separate boolean derived from
 * the status FLAGS. The two are rendered as two facts — the figure, and the
 * word beside it — because folding them makes **a draft indistinguishable from
 * a fully-spent note**, which is the one confusion this column exists to
 * prevent. See `RemainingCell`.
 *
 * 🔴 **NO AGGREGATE ANYWHERE ON THIS PAGE, AND THAT IS STRUCTURAL.** A balance
 * is per company **per currency**; there is no exchange rate in this system and
 * nothing here converts. One page legitimately holds several codes, so a column
 * total would be wrong by construction rather than merely imprecise. Every
 * figure renders with its currency code for the same reason.
 *
 * ⛔ **NEVER TEST A STATUS NAME.** `status` carries `isEditable`,
 * `isAwaitingApproval`, `isApproved` and `isVoid`, plus the note's own
 * `spendable`. The names are admin-editable labels — this codebase has matched
 * on one three times and been wrong three times (`"Unpaid"` contains `"paid"`).
 * ⚠️ **`isApproved` and `isVoid` ARE NOT COMPLEMENTS**: a note is approved and
 * THEN voided, so both are true on that row and the second is what decides.
 * `statusTone` below is the one place that ordering is written down.
 *
 * ── FIVE THINGS THIS FILE COULD NOT RESOLVE, EACH NAMED WITH WHAT ENDS IT ────
 *
 * Every one of them is a property of another file, and this page is written to
 * be correct — and honest — while they stand.
 *
 * 1. 🔴 **THERE IS NO `/credit-note-statuses` ENDPOINT.** Measured in the
 *    backend on 2026-09-15: `src/routes.ts` mounts `/credit-notes` and the
 *    reference registry serves `credit-note-reasons`; **no segment and no route
 *    serves the status master**, and `f_credit_note_statuses` is read only
 *    through a join inside the credit-note query itself. So the Status funnel
 *    **cannot** read a master and falls back to values accumulated from the
 *    loaded rows. See `STATUS_COLUMN` for why that fallback is the honest one
 *    here and what it costs. **What ends it: that endpoint existing.**
 * 2. ⚠️ **`/credit-note-reasons` EXISTS ON THE WIRE AND THIS CLIENT HAS NO
 *    METHOD FOR IT.** `api.ts` carries no `listCreditNoteReasons`, so the Reason
 *    funnel accumulates too — for a different reason from Status, and a
 *    curable one. **What ends it: that method existing.**
 * 3. ⚠️ **`src/lib/credit-notes.ts` IS BEING WRITTEN IN PARALLEL AND DID NOT
 *    EXIST WHEN THIS FILE WAS.** Nothing here imports from it, because importing
 *    a guessed symbol from a module that does not yet exist breaks the build for
 *    everybody. The wording and the two rules this page needs are in ONE marked
 *    block below (`statusTone`, `SPENDABLE_LABEL`, `HAS_BALANCE_LABEL`,
 *    `IMPORTED_FROM_XERO_OPTIONS`). ⛔ **The moment that module lands, replace
 *    that block with imports rather than leaving a second copy** — two spellings
 *    of "is this note spendable" is exactly the drift it exists to prevent.
 * 4. 🔴 **THE EXIT CONDITION THIS POINT NAMED HAS FIRED — `ModuleSlug` CARRIES
 *    `'credit-notes'` NOW.** ⚠️ *SUPERSEDED: "**`ModuleSlug` HAS NO
 *    `'credit-notes'` MEMBER**, so `ability.canRead`, `ability.canUpdate` and
 *    `denialReason` cannot be asked about this module at all — they would not
 *    typecheck … **What ends it: that union gaining the member.**"* Measured
 *    2026-09-19: the union has 22 members and this is one of them, so all three
 *    CAN be asked now.
 *
 *    ⚠️ **NOTHING HERE IS GATED ON `ability` ANYWAY, AND THAT IS NOW A CHOICE
 *    RATHER THAN A LIMIT — it has not been re-taken.** `GET /credit-notes`
 *    answers the same to everybody authenticated, so a client-side gate would
 *    be a VIEW control, not access control; `CommissionsPage` takes the other
 *    side and renders a refusal panel, worded as *hidden* rather than as
 *    protected. **Put it to the owner rather than adding one on the strength of
 *    the slug existing.**
 *
 *    ⛔ **The borrowed-slug prohibition is UNTOUCHED and is the half that never
 *    depended on the union**: gating on `invoices` would be the
 *    `integrations`-borrowing-`companies` mistake, where granting one capability
 *    silently grants a second nobody was asked about.
 * 5. 🔴 **CREDIT NOTES HAVE A FIFTH ACTION, `approve`, AND `ActionSlug` HAS
 *    FOUR (plus `convert` and the dead `export`).** So even with (4) closed, the
 *    ability model cannot answer "may this person approve". **This page renders
 *    no approve control** — there is no detail surface to put one on — but the
 *    rule for whoever adds it is written here rather than rediscovered: **render
 *    the control and let the 403 arrive.** A hidden control is an optimisation;
 *    the refusal is the guarantee, and a client-side guess at a permission the
 *    model cannot express is a convention dressed as a contract.
 *
 * ── WHY THERE ARE NO WRITE AFFORDANCES ──────────────────────────────────────
 *
 * ⛔ **No New button, no row Edit, no row Delete, and the Subject is TEXT rather
 * than a link.** Not an omission and not a permissions decision: there is no
 * `CreditNoteFormModal` in `src/components/`, and `App.tsx` declares no
 * `/credit-notes/:id` route — both measured 2026-09-15. A button that opens
 * nothing, or a link that lands on `NotFound`, is the affordance-with-one-
 * outcome this repo refuses everywhere (Xero's `sync`, DocuSign's absent
 * connect). ⚠️ **When a detail route or a form lands, the Subject cell and the
 * actions column are the two places to wire them**, and the gates then go on
 * `ability` — see (4) and (5) above for what must exist first.
 *
 * ── ONE WRITER PER QUERY KEY ────────────────────────────────────────────────
 *
 * The rule the whole filter surface is arranged around, because two controls
 * writing one key disagree silently and whichever assignment runs last wins:
 *
 * | key                                    | its ONE writer                     |
 * | -------------------------------------- | ---------------------------------- |
 * | `search`                               | the search box                     |
 * | `statusId`                             | the Status column funnel           |
 * | `creditReasonId`                       | the Reason column funnel           |
 * | `amountMin` / `amountMax`              | the Amount column's range          |
 * | `currency`                             | the filter bank's Combobox         |
 * | `creditNoteDateAfter` / `…Before`      | the filter bank's two date inputs  |
 * | `spendable`, `hasBalance`              | the filter bank's two checkboxes   |
 * | `importedFromXero`                     | the filter bank's Combobox         |
 * | `companyId`, `contactId`,              | arrived at BY LINK — a badge, and  |
 * | `sourceInvoiceId`, `allocatedToInvoiceId` | no control of their own         |
 * | `sort` / `order`                       | the table hook                     |
 *
 * ⚠️ **CURRENCY AND DATE THEREFORE HAVE NO FUNNEL OF THEIR OWN**, and the Date
 * column's funnel is deliberately PAGE-scoped: the bank owns the server-side
 * window, so the funnel asks the neighbouring question ("which of these twenty
 * dates") rather than competing for the same key. `/invoices` carries the same
 * pairing on Bill date, for the same reason.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useRememberedState } from '@/lib/filter-memory/use-filter-memory';
import { useSearchParams } from 'react-router-dom';
// Shape AND existence, so '2026-02-30' is caught here rather than as a 422
// naming the query parameter. The app's one copy of that rule.
import { isRealDate } from '@/components/catalog/form-utils';
import { ChevronIcon, SearchIcon } from '@/components/catalog/icons';
import { ApiError, api } from '@/lib/api';
import { cx } from '@/lib/cx';
import { formatDateOnly, formatDateTime } from '@/lib/format';
import { documentCustomerName } from '@/lib/company-names';
import { Money } from '@/components/ui/Money';
import { Notice } from '@/components/ui/Notice';
import type {
  CreditNote,
  CreditNoteListParams,
  CreditNoteListSort,
  Paginated,
} from '@/lib/types';
import { creditNoteStatusTone } from '@/lib/credit-notes';
import { useAuth } from '@/lib/use-auth';
// The row Submit for approval / Approve controls and their ONE dialog — the same
// hook the company page's Credit notes tab uses (owner, 2026-10-28).
import { useCreditNoteRowActions } from '@/components/companies/CompanyDocumentsHistory';
// The currency master — the one filter vocabulary on this page that CAN be read
// from a master today. See `useCurrencyOptions`' own header for why it sends
// `isActive: true` where the status pickers do not.
import { currencyOptions, useCurrencyOptions } from '@/lib/use-invoice-options';
import { useTableFilters, type ColumnSpec } from '@/lib/use-table-filters';
import {
  Badge,
  Button,
  Checkbox,
  Combobox,
  EmptyState,
  Field,
  FilterableTH,
  FilterSheet,
  Input,
  PageHeader,
  Panel,
  PanelFooter,
  Skeleton,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
  type BadgeTone,
} from '@/components/ui';

/** `perPage` on `/credit-notes` is 1..100 and defaults to 20. */
const PER_PAGE = 20;
const SKELETON_ROWS = 6;

/* ========================================================================== *
 * THE BLOCK THAT MOVES TO `src/lib/credit-notes.ts`
 *
 * ⚠️ **EVERYTHING BETWEEN THESE TWO MARKERS IS A PLACEHOLDER FOR THAT MODULE**,
 * which was being written in parallel and did not exist when this file was.
 * Each entry is a RULE or a WORD that more than one surface will need, which is
 * precisely what belongs in a shared module rather than in a page.
 *
 * ⛔ **WHEN IT LANDS, DELETE THIS BLOCK AND IMPORT.** Leaving it as a second
 * copy is worse than never having written it: two spellings of "is this note
 * spendable" agree on the day they are written and drift on the first
 * correction, and what drifts is a statement about money a customer is owed.
 * ========================================================================== */

/**
 * The badge tone for a status, **decided by the FLAGS and in this order**.
 *
 * ⛔ **`isVoid` IS TESTED FIRST AND THAT IS THE WHOLE RULE.** `isApproved` and
 * `isVoid` are not complements — a note is approved and then voided, so both
 * are true on that row — and the second is what decides. Testing `isApproved`
 * first paints a withdrawn note green, which is the reassuring direction and
 * therefore the one nobody reports.
 *
 * ⛔ **AND NOTHING HERE READS `status.name`.** The names are admin-editable.
 */
/*
 * ✅ MOVED to `creditNoteStatusTone` in `@/lib/credit-notes` (2026-09-24), where
 * the company page's Credit block reads it too — one rule, two surfaces.
 */
const statusTone: (status: CreditNote['status']) => BadgeTone = creditNoteStatusTone;

/**
 * The two balance filters, worded from what the server actually does.
 *
 * ⚠️ **BOTH ARE SINGLE CHECKBOXES, NOT THREE-STATE CONTROLS, AND THAT IS THE
 * WIRE'S DECISION RATHER THAN A SIMPLIFICATION.** The repository tests
 * `=== true` on each, so **`?spendable=false` and `?hasBalance=false` NARROW
 * NOTHING** — they are the same request as omitting the key. A tri-state
 * control would therefore offer a third position the query string cannot
 * express: a reader picking "No" would meet the unfiltered list under a control
 * reading "No", which is a filter that reports itself as applied and is not.
 * So: ticked sends `true`, unticked omits the key, and there is no third.
 *
 * ⚠️ **`importedFromXero` IS THE OPPOSITE AND GETS THREE OPTIONS** — that column
 * is a timestamp or NULL with no third state, so the NULL *is* the answer rather
 * than the absence of one and both directions genuinely partition the list. The
 * repository tests `!== undefined` there and `=== true` on the two above. Three
 * keys one line apart, two different rules; this is why they are not one
 * control.
 */
const SPENDABLE_LABEL = 'Counts towards a balance';
const HAS_BALANCE_LABEL = 'Has credit left';

/**
 * ⚠️ **THE VALUES ARE THE WIRE'S, not the reader's words.** They go into the
 * query string verbatim, so a value of `'Yes'` would send
 * `?importedFromXero=Yes` — a 422 that renders as an empty table, which reads as
 * "nothing matches" rather than as a bad option.
 *
 * `''` is the absence of the key, and it is a THIRD request distinct from both.
 */
const IMPORTED_FROM_XERO_OPTIONS = [
  { value: 'true', label: 'Imported from Xero' },
  { value: 'false', label: 'Raised in EMERGE' },
] as const;

/* ========================================================================== *
 * END OF THE BLOCK THAT MOVES TO `src/lib/credit-notes.ts`
 * ========================================================================== */

/* -------------------------------------------------------------------------- *
 * The URL-backed filters
 * -------------------------------------------------------------------------- */

/**
 * The id filters, **every one of which is arrived at by LINK rather than set
 * here**, and each shown as a badge that can be cleared.
 *
 * ⚠️ **`sourceInvoiceId` AND `allocatedToInvoiceId` ARE TWINS THAT ROUTINELY
 * DISAGREE, AND NEITHER IS THE OTHER'S FALLBACK.** One asks what the credit was
 * RAISED FROM, the other what it PAID OFF — a note raised from invoice 7 may be
 * spent entirely against invoice 9, and an imported note has no source line at
 * all. Sending both is legal and means both. The badges name them in those
 * words for exactly that reason; "invoice 7" alone would be a confident wrong
 * answer about which question was asked.
 */
const ID_FILTERS = [
  'companyId',
  'contactId',
  'sourceInvoiceId',
  'allocatedToInvoiceId',
] as const;
type IdFilter = (typeof ID_FILTERS)[number];

/**
 * The `credit_note_date` window, both bounds **INCLUSIVE**, so the range names
 * the two days at its ends rather than the gap between them.
 *
 * ⚠️ **AN UNDATED NOTE MATCHES NEITHER BOUND**, so the pair does not partition
 * the list — `credit_note_date` is nullable and there is no `OR … IS NULL`. The
 * only way to reach an undated note is the Date column's own Is empty box.
 *
 * `''` means "no bound" and the key is OMITTED: `?creditNoteDateAfter=` is a 422
 * on this `.strict()` schema, not "no filter".
 */
const DATE_FILTERS = ['creditNoteDateAfter', 'creditNoteDateBefore'] as const;
type DateFilter = (typeof DATE_FILTERS)[number];

/**
 * Reads a positive integer out of the query string, ignoring anything else.
 *
 * Blank and malformed values are dropped rather than forwarded: `?companyId=` is
 * a 422 on this endpoint, not "no filter", and a pasted `?companyId=abc` would
 * take the whole list down with it — which renders as an empty table and reads
 * as "this company has no credit notes".
 */
function parseId(raw: string | null): number | null {
  if (!raw) return null;
  return /^[1-9]\d*$/.test(raw) ? Number(raw) : null;
}

/**
 * `currency` is validated as exactly `^[A-Z]{3}$` server-side — `usd` is a 422,
 * not folded to uppercase — so anything else in the URL is treated as absent.
 */
function parseCurrency(raw: string | null): string | null {
  if (!raw) return null;
  return /^[A-Z]{3}$/.test(raw) ? raw : null;
}

/**
 * A date bound, or `''` for none.
 *
 * ⚠️ **No `Date` is ever constructed from one of these**, here or below:
 * `new Date('2026-05-31')` is midnight UTC and renders as 30 May west of
 * Greenwich. They are compared as strings and formatted by `formatDateOnly`.
 */
function dateBound(raw: string | null): string {
  return raw !== null && isRealDate(raw) ? raw : '';
}

/** `?spendable=true` and nothing else counts. See `SPENDABLE_LABEL`. */
function parseTrueFlag(raw: string | null): boolean {
  return raw === 'true';
}

/** `''` for absent — a third state distinct from both `'true'` and `'false'`. */
function parseTriFlag(raw: string | null): string {
  return raw === 'true' || raw === 'false' ? raw : '';
}

/* -------------------------------------------------------------------------- *
 * The columns
 * -------------------------------------------------------------------------- */

const SUBJECT_COLUMN = 'subject';
const COMPANY_COLUMN = 'company';
const STATUS_COLUMN = 'statusId';
const REASON_COLUMN = 'creditReasonId';
const DATE_COLUMN = 'creditNoteDate';
const CURRENCY_COLUMN = 'currency';
const AMOUNT_COLUMN = 'amount';
const ALLOCATED_COLUMN = 'allocated';
const REMAINING_COLUMN = 'remaining';
const CREATED_COLUMN = 'createdAt';

/**
 * Every column, in order, with the one width each contributes to the table's
 * floor.
 *
 * ✅ **ONE ARRAY DRIVES THE HEADER, THE BODY, THE SKELETON, THE MOBILE FILTER
 * SHEET AND THE MINIMUM WIDTH.** `/invoices` records what four parallel lists
 * cost — a skeleton cell outliving its column shifts every column after it as
 * the rows arrive, and a hand-carried `min-w-[…]` literal sat stale by 16px for
 * nine days. **None of that errors and none of it is visible to `tsc`.**
 *
 * ⚠️ **The Subject column carries no width** — it is the one FLUID column and
 * takes whatever the fixed ones leave. Its contribution to the floor comes from
 * `SUBJECT_MIN_CONTENT` instead, which is also the content minimum on its cell:
 * under `table-layout: auto` a column whose content can break mid-word has a
 * min-content of one character, so it is the column the algorithm shrinks FIRST
 * and a long subject wraps to five lines with slack still on the row.
 */
const SUBJECT_MIN_CONTENT = 240;
/** `TD`'s `px-3`, twice. A column's width is its content minimum plus this. */
const CELL_PADDING_X = 24;

interface ColumnMeta {
  label: string;
  width: number;
  numeric?: boolean;
  /** Right-aligns nothing; it decides whether a funnel renders at all. */
  filterable?: boolean;
}

/**
 * Every column key this page declares.
 *
 * ⚠️ IT IS SPELLED OUT RATHER THAN DERIVED FROM `COLUMN_KEYS`, WHICH IS
 * DECLARED BELOW. Deriving it would be circular: `COLUMN_KEYS` is the ORDER and
 * `COLUMNS` is the DEFINITION, and the order is built from the definitions. The
 * two are kept in step by `Record<ColumnKey, ColumnMeta>` — a key here with no
 * entry is a compile error, and an entry with no key is one too.
 */
type ColumnKey =
  | typeof SUBJECT_COLUMN
  | typeof COMPANY_COLUMN
  | typeof STATUS_COLUMN
  | typeof REASON_COLUMN
  | typeof DATE_COLUMN
  | typeof CURRENCY_COLUMN
  | typeof AMOUNT_COLUMN
  | typeof ALLOCATED_COLUMN
  | typeof REMAINING_COLUMN
  | typeof CREATED_COLUMN;

/*
 * ⚠️ KEYED ON `ColumnKey`, NOT ON `string`, AND THE DIFFERENCE WAS THIRTEEN
 * COMPILE ERRORS.
 *
 * This app builds with `noUncheckedIndexedAccess`, so indexing a
 * `Record<string, …>` yields `ColumnMeta | undefined` — every `COLUMNS[key]`
 * below, including the one that sums the table's floor, became possibly
 * undefined even though every key is a literal declared ten lines up.
 *
 * ⛔ Two repairs were rejected. A `!` at each site asserts away a real check
 * thirteen times, and would go on silently asserting it if somebody removed a
 * column from this object but not from `COLUMN_KEYS`. A bare `satisfies` keeps
 * the INFERRED literal type, under which `numeric` and `filterable` do not
 * exist at all on the entries that omit them — four errors in place of
 * thirteen, and the wrong shape besides: those two are OPTIONAL properties of
 * every column, not properties only some columns have.
 */
const COLUMNS: Record<ColumnKey, ColumnMeta> = {
  [SUBJECT_COLUMN]: {
    label: 'Subject',
    width: SUBJECT_MIN_CONTENT + CELL_PADDING_X,
    filterable: true,
  },
  [COMPANY_COLUMN]: { label: 'Company', width: 200, filterable: true },
  [STATUS_COLUMN]: { label: 'Status', width: 160, filterable: true },
  [REASON_COLUMN]: { label: 'Reason', width: 180, filterable: true },
  [DATE_COLUMN]: { label: 'Date', width: 130, filterable: true },
  /*
   * ⛔ **NO FUNNEL, DELIBERATELY.** `currency` has exactly one writer on this
   * page — the Combobox in the filter bank — and a column owning the same key
   * would be two writers of one parameter. It has no `sort` member either, so
   * there is nothing else a funnel here could offer.
   */
  [CURRENCY_COLUMN]: { label: 'Currency', width: 110 },
  [AMOUNT_COLUMN]: { label: 'Amount', width: 150, numeric: true, filterable: true },
  /*
   * ⛔ **NO FUNNEL ON EITHER DERIVED FIGURE.** There is no `allocatedMin`, no
   * `remainingMin` and no sort member for either — the adjacent question the API
   * *does* answer is `?hasBalance=true`, which is a boolean in the filter bank
   * rather than a bound on this column. A funnel here would offer the distinct
   * figures on twenty loaded rows under a heading that implies the whole table.
   */
  [ALLOCATED_COLUMN]: { label: 'Allocated', width: 150, numeric: true },
  [REMAINING_COLUMN]: { label: 'Remaining', width: 180, numeric: true },
  [CREATED_COLUMN]: { label: 'Created', width: 150, filterable: true },
};

const COLUMN_KEYS = [
  SUBJECT_COLUMN,
  COMPANY_COLUMN,
  STATUS_COLUMN,
  REASON_COLUMN,
  DATE_COLUMN,
  CURRENCY_COLUMN,
  AMOUNT_COLUMN,
  ALLOCATED_COLUMN,
  REMAINING_COLUMN,
  CREATED_COLUMN,
] as const;

/** Summed from the declared widths, so the floor cannot drift from the header. */
const TABLE_MIN_WIDTH = COLUMN_KEYS.reduce((total, key) => total + COLUMNS[key].width, 0);

/**
 * The Actions column (owner, 2026-10-28: Submit for approval / Approve). Kept
 * OUT of `COLUMN_KEYS` because it has no filter, sort or `FilterSheet` entry;
 * rendered only for a reader who may do at least one of the two, and added to
 * the floor only then.
 */
const ACTIONS_WIDTH = 96;

/**
 * The filterable columns, built per render because two of them need data that
 * only the loaded rows can supply.
 *
 * `statusNames` and `reasonNames` map an id to the label the popover shows. They
 * are derived from the rows rather than from a master — see the file header,
 * points (1) and (2) — which is why they are passed in rather than imported.
 */
const creditNoteColumns = (
  statusNames: ReadonlyMap<string, string>,
  reasonNames: ReadonlyMap<string, string>,
): ColumnSpec<CreditNote>[] => [
  /**
   * Subject — **sort only, in effect**.
   *
   * ⚠️ The popover still lists the distinct subjects on the loaded page, which
   * is the standard page-scoped behaviour and is nearly useless on a column
   * whose values are almost unique. It is kept because the SORT is real and the
   * funnel is the only place `FilterableTH` hangs one. ⛔ **It writes no query
   * key**, so it cannot conflict with the search box, which owns `search` and
   * asks a different question (a LIKE across the whole table, not an exact value
   * on this page).
   */
  {
    key: SUBJECT_COLUMN,
    label: COLUMNS[SUBJECT_COLUMN].label,
    get: (note) => note.subject,
    sortKey: 'subject' satisfies CreditNoteListSort,
  },
  /**
   * Company — page-scoped values, server-side sort.
   *
   * ⛔ **NO `param`, DELIBERATELY.** `companyId` arrives by LINK (a badge in the
   * filter bank) so that `/credit-notes?companyId=22` off a company record is a
   * real link; a funnel emitting the same key would be its second writer. The
   * funnel therefore narrows the loaded page only, which is the honest thing a
   * column can do for a key somebody else owns.
   *
   * ⚠️ **`sortKey: 'company'` ORDERS BY THE COMPANY NAME, never `company_id`** —
   * the server says so explicitly. An id order groups just as correctly and
   * reads as random.
   */
  {
    key: COMPANY_COLUMN,
    label: COLUMNS[COMPANY_COLUMN].label,
    get: (note) => documentCustomerName(note.company),
    sortKey: 'company' satisfies CreditNoteListSort,
  },
  /**
   * Status — a **real server filter** on `?statusId=`, owned outright by this
   * funnel.
   *
   * 🔴 **`options` IS ABSENT AND THAT IS NOT AN OVERSIGHT: THERE IS NO STATUS
   * MASTER TO READ.** Measured in the backend 2026-09-15 — `src/routes.ts`
   * mounts `/credit-notes`, the reference registry serves `credit-note-reasons`,
   * and **nothing serves `f_credit_note_statuses`**; it reaches a client only as
   * the `status` object joined onto each note.
   *
   * So the hook accumulates the vocabulary from rows it has seen
   * (`seenServerValues`), and **that is the honest fallback here** rather than a
   * declared closed set, for a reason worth stating: a declared set would mean
   * writing the four status NAMES into this file — which is matching on an
   * admin-editable label by another route, and it would assert a vocabulary this
   * client has never been told. An accumulated list is narrower than the truth
   * and never claims otherwise.
   *
   * ⚠️ **THE COST, STATED:** a status absent from every page somebody has
   * looked at is a status they cannot filter by, and the popover gives no hint
   * that anything is missing — worst on a rare status, which is exactly the one
   * worth finding. `seenServerValues` is also what stops the filter being
   * one-way: once applied, the next response holds that status alone, and a list
   * derived from `rows` would collapse to the one value already chosen.
   *
   * ✅ **`multi` since 2026-10-28** (owner: every filter takes several values).
   * *SUPERSEDED: "NO `multi` — `?statusId=1,2` is a 422".* The endpoint now
   * parses a comma-separated list and ORs it, so several ticks query the whole
   * table rather than narrowing the loaded page.
   *
   * `get` returns the id because that is what the param takes; `format` turns it
   * back into the name for the popover.
   */
  {
    key: STATUS_COLUMN,
    label: COLUMNS[STATUS_COLUMN].label,
    get: (note) => note.status.id,
    format: (value) => statusNames.get(value) ?? `#${value}`,
    scope: 'server',
    param: 'statusId',
    multi: true,
    sortKey: 'status' satisfies CreditNoteListSort,
  },
  /**
   * Reason — `?creditReasonId=`, and the same accumulation as Status **for a
   * different and curable reason**.
   *
   * ⚠️ `/credit-note-reasons` IS a live reference-registry segment on the wire;
   * what is missing is a client method for it (`api.ts` carries no
   * `listCreditNoteReasons`). So unlike Status, this one becomes a proper master
   * read the moment that method exists — at which point the fix is `options:`
   * here and nothing else. **Do not "fix" it by declaring the reason names**:
   * they are admin-editable rows and a literal list would be wrong the first
   * time somebody adds one.
   *
   * ✅ **SORTED ON THE SERVER since 2026-10-09** — `reason` (backend
   * `32057ed`), ordered by the reason's NAME, never its id. *SUPERSEDED: "⛔ **NO
   * `sortKey`** — `creditReason` is not a member of this endpoint's sort enum".*
   */
  {
    key: REASON_COLUMN,
    label: COLUMNS[REASON_COLUMN].label,
    get: (note) => note.creditReason.id,
    format: (value) => reasonNames.get(value) ?? `#${value}`,
    scope: 'server',
    param: 'creditReasonId',
    // A comma-separated list ORs (2026-10-28), as on Status above.
    multi: true,
    sortKey: 'reason' satisfies CreditNoteListSort,
  },
  /**
   * Date — **page-scoped values, and that is the point**.
   *
   * The filter bank owns the server-side window
   * (`creditNoteDateAfter`/`Before`), so this funnel asks the neighbouring
   * question: which of the dates on THIS page. Two questions about one column,
   * not two writers of one key — `/invoices` carries the identical pairing on
   * Bill date.
   *
   * ⚠️ **Its Is empty box is the only way to reach an undated note**, because
   * the two bounds are inclusive and a NULL matches neither.
   */
  {
    key: DATE_COLUMN,
    label: COLUMNS[DATE_COLUMN].label,
    get: (note) => note.creditNoteDate,
    // The stored 'YYYY-MM-DD' is the filter VALUE; the reader sees it formatted
    // exactly as the cell renders it. No `Date` is constructed either side.
    format: (value) => formatDateOnly(value),
    sortKey: 'creditNoteDate' satisfies CreditNoteListSort,
  },
  /**
   * Amount — the note's `grandTotal`, as a **numeric range** over the API's own
   * `amountMin` / `amountMax` pair.
   *
   * ⚠️ **`signed: true`, FROM THE WIRE RATHER THAN FROM TASTE.** The server's
   * `money()` is `/^-?\d{1,12}(\.\d{1,2})?$/` — it accepts a leading minus — so
   * an unsigned control would refuse a bound the API takes. This repo's rule is
   * that a wrongly-unsigned column refuses a value in front of the person typing
   * it (visible) while a wrongly-signed one hides rows (silent), and the pattern
   * is the authority on which this is.
   *
   * ⚠️ **`decimal: true`**: these are money bounds and reach the wire as the
   * exact strings they were typed as. ⛔ Nothing parses, rounds or reformats one.
   *
   * ⚠️ **BOTH BOUNDS ARE INCLUSIVE AND A NULL MATCHES NEITHER** — which cannot
   * arise here (`grand_total` is NOT NULL) but is why the Is empty boxes stay on
   * a numeric column generally.
   */
  {
    key: AMOUNT_COLUMN,
    label: COLUMNS[AMOUNT_COLUMN].label,
    get: (note) => note.grandTotal,
    numeric: true,
    range: { minParam: 'amountMin', maxParam: 'amountMax', signed: true, decimal: true },
    sortKey: 'amount' satisfies CreditNoteListSort,
  },
  /**
   * Created — sort only, page-scoped values.
   *
   * ⚠️ `createdAt` is a real TIMESTAMP, unlike the plain `YYYY-MM-DD` on the
   * Date column above, so it is formatted with `formatDateTime`. The two look
   * interchangeable and are not.
   */
  {
    key: CREATED_COLUMN,
    label: COLUMNS[CREATED_COLUMN].label,
    get: (note) => note.createdAt,
    format: (value) => formatDateTime(value),
    sortKey: 'createdAt' satisfies CreditNoteListSort,
  },
];

/* -------------------------------------------------------------------------- *
 * Cells
 * -------------------------------------------------------------------------- */

function CreditNoteIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4"
    >
      <path d="M4 4h16v16l-3-2-3 2-3-2-3 2V4Z" />
      <path d="M9 10h6M9 14h4" />
    </svg>
  );
}

/*
 * Money figures render through the shared `<Money>` — **with the currency code,
 * always**. ⛔ THE CODE IS NOT DECORATION: a balance is per company per currency,
 * so a bare number beside another bare number is an invitation to add two
 * currencies together. It groups the decimal string without touching a float.
 */

/**
 * Remaining — **the figure AND whether it can be spent, as two facts**.
 *
 * 🔴 **`remainingAmount` IS REPORTED EVEN ON A DRAFT**, where it is the note's
 * whole value and can be allocated against nothing. `spendable` is the separate
 * boolean that decides, and it is derived from the status flags rather than from
 * this figure.
 *
 * ⛔ **NEITHER MAY STAND FOR THE OTHER.** Rendering the figure alone makes a
 * draft look like available credit; rendering a dash when `spendable` is false
 * makes a draft indistinguishable from a note that has been spent down to zero.
 * Both are on screen, and the word is real text rather than a colour — the row
 * has to read correctly in greyscale and to a screen reader.
 *
 * ⚠️ **The badge is NOT helper text.** It names a state the reader cannot
 * otherwise see: two rows showing `500.00 GBP` where one is spendable and one is
 * not are identical without it.
 */
function RemainingCell({ note }: { note: CreditNote }) {
  return (
    <span className="inline-flex flex-wrap items-center justify-end gap-1.5">
      <Money amount={note.remainingAmount} currency={note.currency} />
      {!note.spendable && <Badge tone="neutral">Not spendable</Badge>}
    </span>
  );
}

function CellContent({ columnKey, note }: { columnKey: string; note: CreditNote }) {
  switch (columnKey) {
    case SUBJECT_COLUMN:
      return (
        /*
         * ⛔ **TEXT, NOT A LINK.** `App.tsx` declares no `/credit-notes/:id`
         * route (measured 2026-09-15), so a link here would land on `NotFound` —
         * which reads as the record being missing rather than as the page not
         * existing. **This is the first place to wire a detail route when one
         * lands.**
         *
         * The content minimum is an INLINE STYLE rather than `min-w-[240px]`,
         * and that is forced: Tailwind v4 scans SOURCE TEXT, so an interpolated
         * class emits no rule at all — the class lands in the DOM and does
         * nothing, which looks exactly like a column that chose not to hold its
         * width.
         */
        <div style={{ minWidth: SUBJECT_MIN_CONTENT }}>
          <p className="m-0 text-sm font-medium text-fg [overflow-wrap:anywhere]">{note.subject}</p>
          {/* Xero's own number, when there is one. Nullable, and most rows will
              not have it. */}
          {note.xeroCreditNoteNumber && (
            <p className="mt-0.5 m-0 truncate text-xs text-fg-muted tabular">
              <span className="sr-only">Xero credit note number </span>
              {note.xeroCreditNoteNumber}
            </p>
          )}
        </div>
      );
    case COMPANY_COLUMN:
      return <span className="text-sm text-fg-secondary">{documentCustomerName(note.company)}</span>;
    case STATUS_COLUMN:
      /* The NAME is rendered and the TONE comes from the flags — see
         `statusTone`, where the `isVoid`-before-`isApproved` ordering is. */
      return <Badge tone={statusTone(note.status)}>{note.status.name}</Badge>;
    case REASON_COLUMN:
      return (
        <span className="text-sm text-fg-secondary" {...(note.creditReasonDetail ? { title: note.creditReasonDetail } : {})}>
          {note.creditReason.name}
        </span>
      );
    case DATE_COLUMN:
      return <span className="whitespace-nowrap tabular">{formatDateOnly(note.creditNoteDate)}</span>;
    case CURRENCY_COLUMN:
      return <span className="tabular">{note.currency}</span>;
    case AMOUNT_COLUMN:
      return <Money amount={note.grandTotal} currency={note.currency} />;
    case ALLOCATED_COLUMN:
      return <Money amount={note.allocatedTotal} currency={note.currency} />;
    case REMAINING_COLUMN:
      return <RemainingCell note={note} />;
    case CREATED_COLUMN:
      return <span className="whitespace-nowrap tabular text-xs">{formatDateTime(note.createdAt)}</span>;
    default:
      return null;
  }
}

/** Skeletons in the SHAPE of the real row, never a spinner. */
function SkeletonCellContent({ columnKey }: { columnKey: string }) {
  switch (columnKey) {
    case SUBJECT_COLUMN:
      return (
        <div className="flex flex-col gap-1.5">
          <Skeleton className="h-3 w-48" />
          <Skeleton className="h-2.5 w-24" />
        </div>
      );
    case STATUS_COLUMN:
      return <Skeleton className="h-4 w-20" />;
    case AMOUNT_COLUMN:
    case ALLOCATED_COLUMN:
    case REMAINING_COLUMN:
      return <Skeleton className="ml-auto h-3 w-24" />;
    case CURRENCY_COLUMN:
      return <Skeleton className="ml-auto h-3 w-10" />;
    default:
      return <Skeleton className="h-3 w-24" />;
  }
}

/* -------------------------------------------------------------------------- *
 * The page
 * -------------------------------------------------------------------------- */

export function CreditNotesPage() {
  /*
   * ⚠️ **NO `useAuth()` AND NO `ability` CALL. IT IS STILL DELIBERATE, FOR A
   * DIFFERENT REASON THAN THE ONE WRITTEN HERE.** ⚠️ *SUPERSEDED: "`ModuleSlug`
   * has no `'credit-notes'` member, so `canRead`, `canUpdate` and
   * `denialReason` cannot be asked about this module."* **It has the member**
   * (measured 2026-09-19) — so the question is now askable and is simply not
   * asked. What survives is the second half: **there is no write affordance on
   * this page to gate.**
   * ⛔ **Do not reach for a neighbouring module's slug** (`invoices`) to fill
   * the gap: that is the borrowed-gate mistake, where a grant for one capability
   * silently carries a second nobody was asked about. See the file header,
   * points (4) and (5).
   *
   * 🔴 **THE READ *IS* PROTECTED, AND THE OPPOSITE CLAIM STOOD HERE AND WAS
   * LOAD-BEARING.** ⚠️ *SUPERSEDED: "**The READ is not protected here either
   * way.** `GET /credit-notes` answers the same to everybody who is
   * authenticated; hiding a page is not refusing a request, and nothing on this
   * screen may be worded as though the data were protected."* **Measured in the
   * backend 2026-09-19**: `credit-notes.service.ts` calls
   * `requirePermission(actorId, CREDIT_NOTE_PERMISSIONS.read)` on the list read
   * AND the detail read, and `requirePermission` throws
   * `403 PERMISSION_REQUIRED`. The router carries no `requireRole` precisely so
   * the GRANT is what refuses.
   *
   * ⚠️ **SO A REFUSAL IS AVAILABLE AND IS NOW WORDED AS ONE** — see the error
   * block below, which was framing a 403 as *"Couldn't load credit notes"* over
   * an internal permission slug. **Whether this page should ALSO gate up front
   * (hide before fetching, the way `CommissionsPage` does) is an OWNER
   * DECISION and is deliberately not taken here**: the 403 is the guarantee and
   * a gate is only the optimisation, so nothing is currently wrong — it is one
   * request that a denied reader need not have made.
   *
   * ✅ **2026-10-28 (owner): the page now HAS write affordances** — row Submit
   * for approval and Approve, gated on `ability` inside
   * `useCreditNoteRowActions` (`credit-notes` update / approve, never a borrowed
   * slug). *SUPERSEDED: the "no `useAuth()` and no `ability` call" and "there is
   * no write affordance on this page to gate" above, and header point 5's
   * "This page renders no approve control".* The read itself is still not gated
   * up front.
   */
  const { ability } = useAuth();
  const showActions = ability.canUpdate('credit-notes') || ability.canApprove('credit-notes');

  // No argument: the hook's first parameter is a DEFAULT INIT, not options, so
  // `useSearchParams({ replace: true })` injects a literal `replace=true` into
  // the query string and then into the parsed filters.
  const [searchParams, setSearchParams] = useSearchParams();

  const [result, setResult] = useState<Paginated<CreditNote> | null>(null);
  const [search, setSearch] = useRememberedState('search', '');
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  /*
   * ⚠️ **A SECOND FLAG RATHER THAN A SENTENCE SNIFFED OUT OF `error`.** The
   * status is the fact; the message is the server's prose and is free to change.
   * Matching on its wording would be this codebase's own *never match on a
   * message* rule, arriving on a refusal.
   */
  const [forbidden, setForbidden] = useState(false);
  const [loading, setLoading] = useState(true);

  /*
   * The serialised query is the memo/effect dependency, never the parsed
   * object: a fresh object every render would key the fetch on identity and
   * refetch for ever. A string compares by value.
   */
  const queryString = searchParams.toString();

  const filters = useMemo(() => {
    const current = new URLSearchParams(queryString);
    const ids = {} as Record<IdFilter, number | null>;
    for (const key of ID_FILTERS) ids[key] = parseId(current.get(key));
    const dates = {} as Record<DateFilter, string>;
    for (const key of DATE_FILTERS) dates[key] = dateBound(current.get(key));
    return {
      ...ids,
      ...dates,
      currency: parseCurrency(current.get('currency')),
      spendable: parseTrueFlag(current.get('spendable')),
      hasBalance: parseTrueFlag(current.get('hasBalance')),
      importedFromXero: parseTriFlag(current.get('importedFromXero')),
    };
  }, [queryString]);

  /**
   * One writer for every URL-backed filter.
   *
   * ⚠️ **`replace: true` on every write**: changing a filter is not a
   * navigation, and stacking one history entry per press makes Back mean "undo
   * one filter".
   *
   * ⚠️ **An empty value DELETES the key rather than writing it empty.**
   * `?currency=` is a 422 on this `.strict()` schema, not "no filter".
   */
  const setFilter = useCallback(
    (key: string, value: string) => {
      const params = new URLSearchParams(queryString);
      if (value === '') params.delete(key);
      else params.set(key, value);
      setSearchParams(params, { replace: true });
    },
    [queryString, setSearchParams],
  );

  const clearFilters = useCallback(() => {
    const params = new URLSearchParams(queryString);
    for (const key of [
      ...ID_FILTERS,
      ...DATE_FILTERS,
      'currency',
      'spendable',
      'hasBalance',
      'importedFromXero',
    ]) {
      params.delete(key);
    }
    setSearchParams(params, { replace: true });
    setSearch('');
  }, [queryString, setSearchParams, setSearch]);

  /*
   * `rows` is memoised on `result` because the table hook takes it as an effect
   * dependency; `?? []` alone is a fresh array every render and costs an extra
   * settling pass per render.
   */
  const rows = useMemo(() => result?.data ?? [], [result]);

  /**
   * Id → name for the two accumulating funnels.
   *
   * ⚠️ **DERIVED FROM THE LOADED ROWS BECAUSE THERE IS NOTHING ELSE TO DERIVE
   * THEM FROM** — see the file header, points (1) and (2). A value the page has
   * never seen falls back to `#<id>` in the popover rather than to a guessed
   * name: this is the same idiom the "also filtered by" badges use for an id
   * they cannot name, and it is honest about not knowing.
   */
  const statusNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const note of rows) map.set(String(note.status.id), note.status.name);
    return map;
  }, [rows]);

  const reasonNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const note of rows) map.set(String(note.creditReason.id), note.creditReason.name);
    return map;
  }, [rows]);

  const columns = useMemo(
    () => creditNoteColumns(statusNames, reasonNames),
    [statusNames, reasonNames],
  );

  const table = useTableFilters(rows, columns);
  const { visibleRows, serverParams, sort, headerFor } = table;

  const currencies = useCurrencyOptions();
  const currencyPickerOptions = useMemo(
    () => currencyOptions(currencies.currencies, filters.currency),
    [currencies.currencies, filters.currency],
  );

  /*
   * A stable string for every narrowing that reaches the SERVER, and nothing
   * that does not. A page-scoped funnel changes none of these, so it correctly
   * leaves the pager where the reader put it.
   */
  const serverSignature = JSON.stringify(serverParams);
  const sortSignature = sort ? `${sort.key}:${sort.direction}` : '';

  /**
   * 🔴 **RESET TO PAGE 1 ON EVERY SERVER-SIDE NARROWING.**
   *
   * Filtering from page 3 asks for page 3 of a much shorter result — usually
   * empty — which reads as *"no credit notes match"* when the matches are all on
   * page 1. This repo records `/users` shipping without exactly this reset, and
   * the house rule ("search inputs debounce at 250ms and reset to page 1") had
   * simply never been applied there. **A rule written down once is not a rule
   * applied everywhere.**
   */
  useEffect(() => {
    // oxlint-disable-next-line react/set-state-in-effect
    setPage(1);
  }, [search, queryString, serverSignature, sortSignature]);

  const load = useCallback(
    (signal?: AbortSignal) => {
      setLoading(true);
      const params: CreditNoteListParams = { page, perPage: PER_PAGE };
      // Every key is written only when it has a value: an empty string is a 422
      // on this `.strict()` schema rather than "no filter".
      if (search) params.search = search;

      const current = new URLSearchParams(queryString);
      for (const key of ID_FILTERS) {
        const value = parseId(current.get(key));
        if (value !== null) params[key] = value;
      }
      for (const key of DATE_FILTERS) {
        const value = dateBound(current.get(key));
        if (value) params[key] = value;
      }
      const currency = parseCurrency(current.get('currency'));
      if (currency !== null) params.currency = currency;

      /*
       * ⚠️ **ONLY EVER SENT AS `true`.** The repository tests `=== true` on
       * both, so `?spendable=false` narrows nothing — omitting the key is the
       * same request, and sending `false` would be a parameter that reports
       * itself as a filter and is not.
       */
      if (parseTrueFlag(current.get('spendable'))) params.spendable = true;
      if (parseTrueFlag(current.get('hasBalance'))) params.hasBalance = true;

      /*
       * ⚠️ **BOTH DIRECTIONS GENUINELY NARROW HERE**, unlike the two above: the
       * column is a timestamp or NULL with no third state, so the NULL *is* the
       * answer. `listQuery` keeps a `false` for exactly this.
       */
      const fromXero = parseTriFlag(current.get('importedFromXero'));
      if (fromXero !== '') params.importedFromXero = fromXero === 'true';

      /*
       * The column funnels' server-scoped selections and the Amount range's two
       * bounds. Merged as an object that only ever holds validated keys —
       * `rangeBounds` omits an empty or unusable bound rather than sending it
       * blank, which on an inclusive pair would be a real bound of zero.
       */
      Object.assign(params, serverParams);

      /*
       * ⚠️ **Sent ONLY when a header has actually been used.** The endpoint's
       * own default is `id`/`desc`; asking for it by name is the same ordering
       * re-spelled as an instruction, which would pin this page to today's
       * default for ever the day the server's moves. Nothing is how you ask for
       * the default.
       *
       * The cast is narrow and deliberate: `sort.key` is the column's own
       * `sortKey`, every one of which is pinned with `satisfies
       * CreditNoteListSort` a few hundred lines up, so a rename is a `tsc` error
       * there instead of a 422 at runtime. *SUPERSEDED 2026-10-09: "There is no
       * exported `CreditNoteListSort` to `satisfies` against yet".*
       */
      if (sort) {
        params.sort = sort.key as CreditNoteListParams['sort'];
        params.order = sort.direction;
      }

      return api
        .listCreditNotes(params, signal)
        .then((data) => {
          setResult(data);
          setError(null);
          setForbidden(false);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setError(caught instanceof Error ? caught.message : 'Failed to load credit notes');
          // The STATUS decides, never the sentence. A 403 here is the grant
          // being absent, and it is refused identically on every retry.
          setForbidden(caught instanceof ApiError && caught.status === 403);
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [page, search, queryString, sort, serverParams],
  );

  // A success re-reads the current page, so the row shows its new status.
  const creditNoteActions = useCreditNoteRowActions(() => void load());
  const tableMinWidth = TABLE_MIN_WIDTH + (showActions ? ACTIONS_WIDTH : 0);

  /*
   * Debounced at 250ms so typing does not fire a request per keystroke, and the
   * controller aborts the in-flight one when the term moves on. The limiter is
   * PER IP, shared by everyone behind the office NAT.
   * ⚠️ *SUPERSEDED: "120 requests per 60 seconds."* It is **12,000/60s**
   * (corrected 2026-09-17). The debounce is the house rule for every search box
   * here regardless of the headroom, and the shared-per-IP shape is unchanged —
   * only the sum that used to justify it is.
   */
  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => void load(controller.signal), 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [load]);

  const total = result?.meta.total ?? 0;
  const totalPages = Math.max(result?.meta.totalPages ?? 1, 1);
  const firstRow = total === 0 ? 0 : (page - 1) * PER_PAGE + 1;
  const lastRow = Math.min(page * PER_PAGE, total);
  /* The first paint only — a refetch under an existing result dims the rows
     rather than replacing them with skeletons, so the page does not flash. */
  const initialLoad = loading && result === null;

  const activeFilterCount =
    ID_FILTERS.filter((key) => filters[key] !== null).length +
    DATE_FILTERS.filter((key) => filters[key] !== '').length +
    (filters.currency !== null ? 1 : 0) +
    (filters.spendable ? 1 : 0) +
    (filters.hasBalance ? 1 : 0) +
    (filters.importedFromXero !== '' ? 1 : 0);
  const anyFilter = activeFilterCount > 0 || search !== '' || table.activeCount > 0;

  const emptyState = (
    <CreditNotesEmptyState filtered={anyFilter} hasError={error !== null} onClear={clearFilters} />
  );

  return (
    <>
      <PageHeader
        eyebrow="Accounts"
        title="Credit notes"
        /*
         * ⛔ **NO ACTIONS SLOT.** There is no create surface to open — see the
         * file header. A "New credit note" button with nothing behind it is an
         * affordance whose only outcome is nothing.
         */
      />

      <Panel>
        {error && (
          <div className="border-b border-border px-4 py-3">
            {/*
             * 🔴 A REFUSAL IS NOT A FAILURE, AND THIS READ IS GENUINELY
             * REFUSABLE — measured in the backend 2026-09-19.
             * `credit-notes.service.ts` calls
             * `requirePermission(actorId, CREDIT_NOTE_PERMISSIONS.read)` on both
             * the list and the detail read, so a reader without the grant gets
             * `403 PERMISSION_REQUIRED` with the message *"Requires the
             * credit-notes.read permission"*.
             *
             * ⚠️ *SUPERSEDED, and it is why this said the wrong thing: the file
             * header's claim that "the READ is not protected here either way;
             * `GET /credit-notes` answers the same to everybody who is
             * authenticated".* It does not.
             *
             * ⛔ **Without the split below, a denied reader met "Couldn't load
             * credit notes." over an internal permission slug** — a failure
             * framing on a refusal, which sends somebody to retry, and then to
             * report a broken page. **Retrying is refused identically for as
             * long as the grant is absent.**
             *
             * The server's own sentence is rendered BESIDE ours rather than
             * instead of it: ours says which KIND of answer this is, theirs says
             * why. Same shape as `CommissionsPage`'s `LoadError`.
             */}
            <Notice
              tone="danger"
              role="alert"
              title={
                forbidden
                  ? 'Your account is not allowed to read credit notes.'
                  : 'Couldn’t load credit notes.'
              }
            >
              <span className="[overflow-wrap:anywhere]">{error}</span>
            </Notice>
          </div>
        )}

        {creditNoteActions.notice && (
          <div className="border-b border-border px-4 py-3">{creditNoteActions.notice}</div>
        )}

        {/* ── The filter bank ────────────────────────────────────────────── */}
        <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3">
          <div className="w-full min-w-0 sm:w-56">
            <Field label="Search credit notes" hideLabel>
              {({ id }) => (
                <Input
                  id={id}
                  type="search"
                  // The API matches `subject` and nothing else — not the Xero
                  // number, not the company. Promising more is a lie the results
                  // immediately expose.
                  placeholder="Search subject…"
                  leadingIcon={<SearchIcon />}
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              )}
            </Field>
          </div>

          <div className="w-full min-w-0 sm:w-40">
            {/* The invalid border alone says nothing; a failed master needs
                words beside it, or an empty list reads as "there are no
                currencies" rather than as a read that did not come back. */}
            <Field
              label="Filter by currency"
              hideLabel
              {...(currencies.failed ? { error: 'Couldn’t load currencies.' } : {})}
            >
              {({ id, 'aria-describedby': describedBy }) => (
                <Combobox
                  id={id}
                  value={filters.currency ?? ''}
                  onChange={(value) => setFilter('currency', value)}
                  options={currencyPickerOptions}
                  emptyOptionLabel="All currencies"
                  placeholder="All currencies"
                  searchPlaceholder="Search currencies…"
                  loading={currencies.loading}
                  aria-describedby={describedBy}
                  {...(currencies.failed ? { invalid: true } : {})}
                />
              )}
            </Field>
          </div>

          <div className="w-full min-w-0 sm:w-48">
            <Field label="Where it came from" hideLabel>
              {({ id }) => (
                <Combobox
                  id={id}
                  value={filters.importedFromXero}
                  onChange={(value) => setFilter('importedFromXero', value)}
                  options={[...IMPORTED_FROM_XERO_OPTIONS]}
                  /* `''` is a THIRD request, distinct from both — it omits the
                     key rather than asking either question. */
                  emptyOptionLabel="Anywhere"
                  placeholder="Anywhere"
                />
              )}
            </Field>
          </div>

          {/*
            * The `credit_note_date` window. Two `type="date"` inputs writing two
            * keys, which is what a RANGE needs — a single funnel value under one
            * key would send a date as a BOUND and return every note on or after
            * it, under a control naming that one day.
            *
            * The From / To labels are visible and are NOT helper text: they name
            * which end of the window each box is, which nothing else on screen
            * says.
            */}
          <div className="flex w-full min-w-0 items-end gap-2 sm:w-auto">
            <div className="min-w-0 flex-1 sm:w-40 sm:flex-none">
              <Field label="From">
                {({ id }) => (
                  <Input
                    id={id}
                    type="date"
                    value={filters.creditNoteDateAfter}
                    onChange={(event) => setFilter('creditNoteDateAfter', event.target.value)}
                  />
                )}
              </Field>
            </div>
            <div className="min-w-0 flex-1 sm:w-40 sm:flex-none">
              <Field label="To">
                {({ id }) => (
                  <Input
                    id={id}
                    type="date"
                    value={filters.creditNoteDateBefore}
                    onChange={(event) => setFilter('creditNoteDateBefore', event.target.value)}
                  />
                )}
              </Field>
            </div>
          </div>

          {/*
            * ⚠️ **TWO CHECKBOXES, NOT TWO TRI-STATE CONTROLS** — see
            * `SPENDABLE_LABEL` for why the wire cannot express a third position
            * on either of these.
            */}
          <Checkbox
            label={SPENDABLE_LABEL}
            checked={filters.spendable}
            onChange={(event) => setFilter('spendable', event.target.checked ? 'true' : '')}
          />
          <Checkbox
            label={HAS_BALANCE_LABEL}
            checked={filters.hasBalance}
            onChange={(event) => setFilter('hasBalance', event.target.checked ? 'true' : '')}
          />

          {anyFilter && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              Clear
            </Button>
          )}
        </div>

        {/*
          * The id filters, which are arrived at by LINK and have no control of
          * their own. A badge each, so a filtered list never looks unfiltered.
          *
          * ⚠️ **THE TWO INVOICE KEYS ARE NAMED APART**, because they ask
          * different questions and routinely disagree — "raised from" is not
          * "allocated to".
          */}
        {ID_FILTERS.some((key) => filters[key] !== null) && (
          <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2 text-xs text-fg-muted">
            <span>Also filtered by</span>
            {filters.companyId !== null && (
              <Badge tone="neutral" variant="outline">
                Company #{filters.companyId}
              </Badge>
            )}
            {filters.contactId !== null && (
              <Badge tone="neutral" variant="outline">
                Contact #{filters.contactId}
              </Badge>
            )}
            {filters.sourceInvoiceId !== null && (
              <Badge tone="neutral" variant="outline">
                Raised from invoice #{filters.sourceInvoiceId}
              </Badge>
            )}
            {filters.allocatedToInvoiceId !== null && (
              <Badge tone="neutral" variant="outline">
                Allocated to invoice #{filters.allocatedToInvoiceId}
              </Badge>
            )}
          </div>
        )}

        <div
          aria-busy={loading || undefined}
          className={cx(
            'transition-opacity duration-150 ease-out motion-reduce:transition-none',
            loading && result && 'opacity-60',
          )}
        >
          {/* ── Below `md`: a stacked card per row ──────────────────────────
              Ten columns will not fit 320px. The card carries every one of
              them: a column that exists only above a breakpoint is
              indistinguishable from one that was removed. */}
          <div className="md:hidden">
            {/*
              * A card has no header row, so every funnel and every sort control
              * would have nowhere to hang below `md`. `FilterSheet` is their
              * mobile form, handed the SAME `headerFor` and `clearAll` the table
              * headers use — one state store rather than two that can disagree.
              */}
            <div className="border-b border-border px-4 py-2">
              <FilterSheet
                keys={COLUMN_KEYS}
                headerFor={headerFor}
                activeCount={table.activeCount}
                clearAll={table.clearAll}
              />
            </div>

            {initialLoad ? (
              <ul className="m-0 list-none p-0">
                {Array.from({ length: SKELETON_ROWS }, (_, index) => (
                  <li key={index} className="border-b border-border px-4 py-3 last:border-b-0">
                    <div className="flex flex-col gap-2">
                      <Skeleton className="h-3 w-44" />
                      <Skeleton className="h-2.5 w-28" />
                      <Skeleton className="h-2.5 w-36" />
                    </div>
                  </li>
                ))}
              </ul>
            ) : visibleRows.length === 0 ? (
              emptyState
            ) : (
              <ul className="m-0 list-none p-0">
                {visibleRows.map((note) => (
                  <li key={note.id} className="border-b border-border px-4 py-3 last:border-b-0">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="m-0 text-sm font-medium text-fg [overflow-wrap:anywhere]">
                          {note.subject}
                        </p>
                        <p className="mt-0.5 m-0 text-xs text-fg-muted">{documentCustomerName(note.company)}</p>
                      </div>
                      <Badge tone={statusTone(note.status)}>{note.status.name}</Badge>
                    </div>

                    {/*
                      * ⚠️ **LABELLED, unlike the desktop cells, and that is the
                      * difference between a table and a card rather than an
                      * inconsistency.** Three money figures under a heading row
                      * mean something; three figures floating on a card mean
                      * nothing. **A label names a value rather than explaining
                      * it**, so it is not helper text — and with three amounts
                      * on one card, the labels are the only thing telling
                      * `Allocated` from `Remaining`.
                      */}
                    <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                      <dt className="m-0 text-fg-muted">Amount</dt>
                      <dd className="m-0 text-right text-fg-secondary">
                        <Money amount={note.grandTotal} currency={note.currency} />
                      </dd>
                      <dt className="m-0 text-fg-muted">Allocated</dt>
                      <dd className="m-0 text-right text-fg-secondary">
                        <Money amount={note.allocatedTotal} currency={note.currency} />
                      </dd>
                      <dt className="m-0 text-fg-muted">Remaining</dt>
                      <dd className="m-0 text-right text-fg-secondary">
                        <RemainingCell note={note} />
                      </dd>
                    </dl>

                    <p className="mt-1.5 m-0 text-xs text-fg-muted">
                      {note.creditReason.name}
                      <span className="mx-1.5 text-fg-subtle">·</span>
                      <span className="tabular">{formatDateOnly(note.creditNoteDate)}</span>
                    </p>
                    <p className="mt-0.5 m-0 text-xs text-fg-muted tabular">
                      Created {formatDateTime(note.createdAt)}
                    </p>
                    {showActions && (
                      <div className="mt-2 flex items-center justify-end gap-1.5 empty:hidden">
                        {creditNoteActions.controls(note)}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* ── From `md`: the table ────────────────────────────────────────
              ONE mount, with only the `<TBody>` swapping. Two branches would
              mean two different header rows and, with them, two different
              measured layouts. */}
          <div className="hidden md:block">
            {!initialLoad && visibleRows.length === 0 ? (
              emptyState
            ) : (
              <TableWrap frozenHeader>
                {/*
                  * ⚠️ **THE FLOOR GOES THROUGH `style`, NOT A CLASS.** Tailwind
                  * v4 scans SOURCE TEXT, so `min-w-[${n}px]` emits no rule at
                  * all — the class lands in the DOM and does nothing, which
                  * reads as a table that simply chose not to hold its floor.
                  * `TABLE_MIN_WIDTH` is summed from `COLUMNS`, so a width change
                  * moves the floor automatically.
                  */}
                <Table style={{ minWidth: tableMinWidth }}>
                  <THead>
                    <TR>
                      {COLUMN_KEYS.map((key) =>
                        COLUMNS[key].filterable ? (
                          <FilterableTH key={key} {...headerFor(key)} />
                        ) : (
                          /* No spec, so `headerFor` would answer with the raw
                             key as a label — these render their own `TH`. */
                          <TH key={key} numeric={COLUMNS[key].numeric}>
                            {COLUMNS[key].label}
                          </TH>
                        ),
                      )}
                      {showActions && (
                        <TH pinned className="text-right" style={{ width: ACTIONS_WIDTH }}>
                          Actions
                        </TH>
                      )}
                    </TR>
                  </THead>

                  {initialLoad ? (
                    <TBody>
                      {Array.from({ length: SKELETON_ROWS }, (_, index) => (
                        <TR key={index}>
                          {COLUMN_KEYS.map((key) => (
                            <TD key={key} numeric={COLUMNS[key].numeric}>
                              <SkeletonCellContent columnKey={key} />
                            </TD>
                          ))}
                          {showActions && (
                            <TD pinned>
                              <Skeleton className="ml-auto h-3 w-12" />
                            </TD>
                          )}
                        </TR>
                      ))}
                    </TBody>
                  ) : (
                    <TBody>
                      {visibleRows.map((note) => (
                        <TR key={note.id} interactive>
                          {COLUMN_KEYS.map((key) => (
                            <TD key={key} numeric={COLUMNS[key].numeric}>
                              <CellContent columnKey={key} note={note} />
                            </TD>
                          ))}
                          {showActions && (
                            <TD pinned className="whitespace-nowrap">
                              <div className="flex items-center justify-end gap-1.5">
                                {creditNoteActions.controls(note)}
                              </div>
                            </TD>
                          )}
                        </TR>
                      ))}
                    </TBody>
                  )}
                </Table>
              </TableWrap>
            )}
          </div>
        </div>

        {/*
          * ⛔ **NO TOTALS ROW, AND IT IS NOT A GAP.** Rows on one page can be in
          * several currencies and there is no exchange rate anywhere in this
          * system, so any sum here would be wrong by construction. A per-currency
          * breakdown is a different feature nobody has asked for; a balance is
          * per company per currency and is not this list's to state.
          */}
        {result && (
          <PanelFooter>
            <p className="m-0 text-center text-xs text-fg-muted tabular sm:text-left">
              {total === 0 ? 'No credit notes to show' : `Showing ${firstRow}–${lastRow} of ${total}`}
            </p>
            <div className="flex items-center gap-2">
              <span className="hidden text-xs text-fg-subtle tabular sm:inline">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="secondary"
                size="sm"
                className="flex-1 sm:flex-none"
                leadingIcon={<ChevronIcon direction="left" />}
                disabled={page <= 1}
                onClick={() => setPage((current) => current - 1)}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                className="flex-1 sm:flex-none"
                trailingIcon={<ChevronIcon direction="right" />}
                disabled={page >= totalPages}
                onClick={() => setPage((current) => current + 1)}
              >
                Next
              </Button>
            </div>
          </PanelFooter>
        )}
      </Panel>
      {creditNoteActions.dialogs}
    </>
  );
}

/**
 * The empty state — three of them, because "nothing came back" has three
 * different causes and only one of them is the reader's to act on.
 *
 * ⛔ **NO COPY HERE IMPLIES DATA IS ON ITS WAY.** A blank list is a permanent,
 * ordinary state in this app, and an empty state that hedges about it is how a
 * dead endpoint gets mistaken for a quiet week — `GET /deliverables` returned a
 * `500` for a day and rendered exactly the documented empty state.
 */
function CreditNotesEmptyState({
  filtered,
  hasError,
  onClear,
}: {
  filtered: boolean;
  hasError: boolean;
  onClear: () => void;
}): ReactNode {
  if (hasError) {
    return (
      <EmptyState
        icon={<CreditNoteIcon />}
        title="Nothing to show"
        /* Points at the banner above rather than restating the server's message
           — one copy of a refusal, in the place that has the whole of it. */
        description="The credit note list couldn’t be loaded. Check the message above and try again."
      />
    );
  }

  return (
    <EmptyState
      icon={<CreditNoteIcon />}
      title={filtered ? 'No credit notes match those filters' : 'No credit notes yet'}
      description={
        filtered
          ? // A picklist id is never validated against the data: an unknown or
            // retired id returns an empty page rather than an error, so "no
            // matches" is not evidence that the filter itself is wrong.
            'Search matches the credit note subject only.'
          : 'Nothing has been credited back yet.'
      }
      action={
        filtered ? (
          <Button variant="secondary" size="sm" onClick={onClear}>
            Clear filters
          </Button>
        ) : null
      }
    />
  );
}
